/**
 * Calendar export utilities for Google Calendar, Outlook Calendar, and .ics files
 */

import moment from 'moment-timezone';

export interface CalendarEvent {
  title: string;
  description?: string;
  location?: string;
  start: Date;
  end: Date;
  timezone?: string;
}

/**
 * Generate Google Calendar URL
 * Converts UTC dates to viewer's timezone for proper display
 */
export function generateGoogleCalendarUrl(event: CalendarEvent): string {
  const formatDate = (date: Date, timezone?: string): string => {
    if (timezone) {
      // Convert UTC date to viewer's timezone and format as YYYYMMDDTHHmmss
      const m = moment.utc(date).tz(timezone);
      return m.format('YYYYMMDDTHHmmss');
    }
    // Fallback to UTC format
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${formatDate(event.start, event.timezone)}/${formatDate(event.end, event.timezone)}`,
  });

  if (event.description) {
    params.append('details', event.description);
  }

  if (event.location) {
    params.append('location', event.location);
  }

  if (event.timezone) {
    params.append('ctz', event.timezone);
  }

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Generate Outlook Calendar URL
 * Outlook expects UTC times in ISO 8601 format with 'Z' suffix
 * The Date objects are already in UTC, representing the correct moment
 */
export function generateOutlookCalendarUrl(event: CalendarEvent): string {
  // Format date as UTC ISO 8601 format (YYYY-MM-DDTHH:mm:ssZ)
  // The Date objects are already UTC, so we format them directly
  const formatDate = (date: Date): string => {
    return date.toISOString().split('.')[0] + 'Z';
  };

  const params = new URLSearchParams({
    subject: event.title,
    startdt: formatDate(event.start),
    enddt: formatDate(event.end),
  });

  if (event.description) {
    params.append('body', event.description);
  }

  if (event.location) {
    params.append('location', event.location);
  }

  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

/**
 * Generate .ics file content
 */
export function generateIcsContent(event: CalendarEvent): string {
  const formatDateUtc = (date: Date): string => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const formatDateLocal = (date: Date, tz?: string): string => {
    if (!tz) {
      return formatDateUtc(date);
    }
    // Convert UTC date to the specified timezone and format as YYYYMMDDTHHmmss
    // The date parameter is a UTC Date object, so we convert it to the target timezone
    const m = moment.utc(date).tz(tz);
    return m.format('YYYYMMDDTHHmmss');
  };

  const escapeText = (text: string): string => {
    return text.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
  };

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//When We Meet//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
  ];

  // Add timezone definition if timezone is provided
  if (event.timezone) {
    lines.push(
      'BEGIN:VTIMEZONE',
      `TZID:${event.timezone}`,
      'END:VTIMEZONE',
    );
  }

  lines.push('BEGIN:VEVENT');

  // Use timezone-aware format if timezone is provided, otherwise use UTC
  if (event.timezone) {
    lines.push(`DTSTART;TZID=${event.timezone}:${formatDateLocal(event.start, event.timezone)}`);
    lines.push(`DTEND;TZID=${event.timezone}:${formatDateLocal(event.end, event.timezone)}`);
  } else {
    lines.push(`DTSTART:${formatDateUtc(event.start)}`);
    lines.push(`DTEND:${formatDateUtc(event.end)}`);
  }

  lines.push(`SUMMARY:${escapeText(event.title)}`);

  if (event.description) {
    lines.push(`DESCRIPTION:${escapeText(event.description)}`);
  }

  if (event.location) {
    lines.push(`LOCATION:${escapeText(event.location)}`);
  }

  lines.push(
    'UID:' + Date.now() + '-' + Math.random().toString(36).substr(2, 9) + '@whenwemeet.com',
    'DTSTAMP:' + formatDateUtc(new Date()),
    'END:VEVENT',
    'END:VCALENDAR',
  );

  return lines.join('\r\n');
}

/**
 * Download .ics file
 */
export function downloadIcsFile(event: CalendarEvent, filename?: string): void {
  const content = generateIcsContent(event);
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `${event.title.replace(/[^a-z0-9]/gi, '_')}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
